import { useState, useEffect, useCallback, useMemo, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Modal } from 'antd';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { BackButton, DemoSelect } from '@/widgets/demo/ListPrimitives';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { recipientsApi } from '@/services/recipients/api';
import { isHandledAuthError, getErrorMessage } from '@/services/client';
import { useToast } from '@/widgets/common';
import {
  RECIPIENT_BOOLEAN_FIELD_KEYS,
  RECIPIENT_CSV_LABELS,
  computeDeceasedMemberCodes,
  computeHideInstructionTextInitial,
  effectiveHouseholdHeadName,
  findHouseholdConsistencyViolations,
  formatRecipientFieldValue,
  householdConsistencyMessage,
  isDetailRowDeceased,
} from '@/data/recipientFields';
import ConfirmDialog from '@/widgets/common/ConfirmDialog';
import NoticeDialog from '@/widgets/common/NoticeDialog';
import DetailErrorSummary, { type DetailErrorEntry } from '@/widgets/common/DetailErrorSummary';
import { detailCellInputClass, FROZEN_LEFT_CLASS, isFrozenDataCol, isLastFrozenDataCol } from '@/widgets/common/detailCellStyles';
import { CASE_STATUS_CODE_MAP, CASE_STATUSES, caseStatusKeyByLabel, caseStatusPermissions, statusResetsOnEdit } from '@/constants/enums';
import {
  APPLICATION_BANK_ACCOUNT_TYPE_OPTIONS,
  bankAccountTypeLabelFromDb,
  bankAccountTypeToDb,
} from '@/utils/bankAccountType';
import {
  bankAccountHolderKanaError,
  bankAccountNumberError,
  bankBranchCodeError,
  bankCodeError,
  sanitizeBankAccountHolderInput,
  sanitizeBankAccountNumberInput,
  sanitizeBankBranchCodeInput,
  sanitizeBankCodeInput,
} from '@/utils/bankInfoValidation';
import { CloseCircleFilled, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { usePermission } from '@/hooks/usePermission';
import { useRecipientDraft, newDraftScope, type RecipientDraft, type NewDraftEntry } from '@/hooks/useRecipientDraft';
import { formatDateTime } from '@/utils/date';
import type { RecipientResponse, RecipientDetailResponse, RecipientUpdateRequest } from '@/services/recipients/types';

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

type RecipientDataLike = Partial<RecipientResponse>;

interface RecipientEditProps {
  data: RecipientDataLike;
  setData: (data: RecipientDataLike) => void;
  returnDetailPageTitle?: string;
  returnBaseCrumb?: { label: string; page: string };
  mode?: 'edit' | 'new';
}

type DetailRowLike = Partial<RecipientDetailResponse> & { fields?: Record<string, unknown> };

// バックエンド (CsvImportService.buildFieldSpecs / RecipientService.parseShort/parseLong) と
// 同じ型・桁数を表現する。
const SHORT_FIELDS = new Set([
  'f2','f3','f4','f5','f6','f7','f8','f9','f10',
  'f19','f21','f22','f23','f24','f79',
]);
const LONG_FIELDS = new Set([
  'f31','f32','f33','f34','f35','f36','f37','f38','f39','f40','f41',
  'f42','f43','f44','f45','f46','f47','f48','f49','f50','f51',
]);
const BOOLEAN_FIELDS = RECIPIENT_BOOLEAN_FIELD_KEYS;

const STRING_FIELD_MAX_LENGTH: Record<string, number> = {
  f1: 255,
  f13: 64, f14: 64, f15: 64, f16: 64, f18: 64, f20: 64,
  f25: 64, f26: 64, f27: 64, f28: 64, f29: 64, f30: 64,
  f52: 256, f53: 16, f54: 512, f55: 256,
  f57: 256, f58: 256, f59: 256,
  f60: 64, f61: 64, f62: 64, f63: 64, f64: 64, f65: 64,
  f77: 128, f78: 128, f80: 32, f81: 128,
};
const BASIC_INFO_FIELD_LABELS = {
  headName: '世帯主氏名',
  addressee: RECIPIENT_CSV_LABELS[51] ?? '決定通知書宛名',
  postalCode: RECIPIENT_CSV_LABELS[52] ?? '決定通知書郵便番号（ハイフンなし）',
  address: RECIPIENT_CSV_LABELS[53] ?? '決定通知書住所',
} as const;
const SHORT_MIN = -32768;
const SHORT_MAX = 32767;
const LONG_MIN = -9223372036854775808n;
const LONG_MAX = 9223372036854775807n;

const isValidShort = (v: string): boolean => {
  if (!/^-?\d+$/.test(v)) return false;
  const n = Number(v);
  return Number.isInteger(n) && n >= SHORT_MIN && n <= SHORT_MAX;
};

const isValidLong = (v: string): boolean => {
  if (!/^-?\d+$/.test(v)) return false;
  try {
    const n = BigInt(v);
    return n >= LONG_MIN && n <= LONG_MAX;
  } catch {
    return false;
  }
};

function normalizeDetailRows(details: DetailRowLike[] = []): Record<string, string>[] {
  return details.map((det) =>
    Object.fromEntries(
      Object.entries(det?.fields || {}).map(([fieldKey, value]) => [
        fieldKey,
        formatRecipientFieldValue(fieldKey, value),
      ]),
    ) as Record<string, string>,
  );
}

// detailRows と並列に保持する行 ID 配列。
// 既存行は number、画面で新規追加された行は null。保存時に null は後端で新規 detail として永続化される。
function extractDetailRowIds(details: DetailRowLike[] = []): (number | null)[] {
  return details.map((det) => (typeof det?.id === 'number' ? det.id : null));
}

// 編集モードの householdFields を組み立てる。
// 決定通知書宛名/郵便番号/住所（f52/f53/f54）は header(recipients) 独立列が真値のため、
// 専用テキストボックスの値をそのまま採用する（明細へは書き戻さない）。
// f55〜f76 は画面上テキストボックスがないため、明細1行目の値をそのまま採用。
// ただし f56（決定通知書教示文非表示）は世帯単位チェックボックスが真値なので household
// フィールドからは除外する（decisionNoticeHideInstructionText で別送）。
// f1 は household_ident_code、f77〜f81 は口座フォーム値が別経路で送られるため除外。
function buildEditHouseholdFields(
  detailRow0: Record<string, string> | undefined,
  textboxOverrides: { f52: string; f53: string; f54: string },
): Record<string, string> {
  const next: Record<string, string> = {};
  if (detailRow0) {
    for (let i = 55; i <= 76; i++) {
      if (i === 56) continue; // f56 はチェックボックスを真値とするため除外
      const k = `f${i}`;
      if (detailRow0[k] != null) next[k] = detailRow0[k];
    }
  }
  next.f52 = textboxOverrides.f52;
  next.f53 = textboxOverrides.f53;
  next.f54 = textboxOverrides.f54;
  return next;
}

const RecipientEdit = ({ data, setData, returnDetailPageTitle = '受給者詳細情報', returnBaseCrumb, mode = 'edit' }: RecipientEditProps) => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const { role, hasPermission } = usePermission();
  const { recipientId: recipientIdParam } = useParams<{ recipientId?: string }>();
  const recipientId = parsePositiveInt(recipientIdParam ?? null);
  const isNew = mode === 'new';
  const baseCrumb = returnBaseCrumb || { label: '受給者／申出書 検索', page: 'recipient-search' };
  const detailOpts = { recipientId: recipientId ?? data?.id ?? undefined, detailPageTitle: returnDetailPageTitle, baseCrumb: baseCrumb };

  const [fetchedData, setFetchedData] = useState<RecipientResponse | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  // 編集モードのデータ取得状態。取得が完了し state へ反映されるまで(formReady=false)はフォームを
  // 表示しない。これにより (a) 前画面で開いた別レコードの stale data 表示と誤保存、
  // (b) 取得中に始めた編集が getById 解決時に上書き消去される問題、を防ぐ。
  const [formReady, setFormReady] = useState(isNew);
  const [loadError, setLoadError] = useState(false);
  // 二重送信ガード(state の再レンダ反映を待たず同期的にロック)。
  const savingRef = useRef(false);

  // 一時保存(下書き)。この端末の localStorage に編集途中の値を保持する。
  //  - 編集モード: 受給者ID単位。
  //  - 新規モード: まだ主キーが無いため、この編集セッション専用の合成 scope(`new:{ID}`)を採番する。
  //    これにより未完成の新規下書きを複数併存できる(他セッションの下書きを上書きしない)。
  //    復元時は activeDraftScope を対象スコープへ切替え、以後の自動保存もそのスロットへ書く。
  const [activeDraftScope, setActiveDraftScope] = useState<string | null>(() => (isNew ? newDraftScope() : null));
  const draftScope = isNew ? activeDraftScope : recipientId;
  const { load: loadDraft, save: saveDraft, clear: clearDraft, listNew: listNewDrafts, removeScope: removeDraftScope } =
    useRecipientDraft(draftScope);
  // 読込時に検出した未適用の下書き(編集モード)。バナーで「復元 / 破棄」を選ぶまで保持し、
  // サーバ値を勝手に上書きしない(stale 表示・誤保存を避ける既存方針に合わせる)。
  const [pendingDraft, setPendingDraft] = useState<RecipientDraft | null>(null);
  // 新規モードの未完成下書き一覧(この端末・このユーザ分)。アクティブスロットは未保存なので含まれない。
  // バナーで各下書きを個別に「復元 / 破棄」できる。
  const [otherNewDrafts, setOtherNewDrafts] = useState<NewDraftEntry[]>(() => (isNew ? listNewDrafts() : []));
  // 最後に下書きを保存した日時(ISO)。アクション欄の「下書き保存済み …」表示に使う。
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    // 受給者データを取得する正当なデータ取得 effect。
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!recipientId) { setLoadError(true); return; }
    let cancelled = false;
    setLoadError(false);
    /* eslint-enable react-hooks/set-state-in-effect */
    recipientsApi.getById(recipientId)
      .then(res => {
        if (cancelled) return;
        setFetchedData(res.data);
        setData(res.data);
      })
      .catch(err => {
        if (cancelled || isHandledAuthError(err)) return;
        setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [recipientId, isNew, setData]);

  const d = isNew ? {} : (fetchedData ?? data ?? {});

  // commentOnly は (1) 非コメント編集権限なし、または (2) 現在ステータスが 5 完了系のいずれかで true。
  // (2) は backend `RecipientService.assertRecipientEditable` と整合させ、保存時の 403 を防ぐ。
  // statusBlocksNonCommentEdit は「非コメント編集権限を持つロールが、ステータスにより編集を
  // 失っている」場合のみ true にする。コールセンター（一般）のように元から非コメント編集権限が
  // ないロールは、ステータスに関係なくコメント編集可能なので status 起因として扱わない。
  const editStatusKey = caseStatusKeyByLabel(d?.paymentState);
  const editIsWelfareEnded = ((d?.welfareStatus ?? '') as string).includes('廃止');
  const statusBlocksNonCommentEdit = !isNew
    && hasPermission('RECIPIENT_EDIT_NON_COMMENT')
    && editStatusKey != null
    && !caseStatusPermissions(editStatusKey, editIsWelfareEnded, role).canEdit;
  const commentOnly = !isNew
    && (!hasPermission('RECIPIENT_EDIT_NON_COMMENT') || statusBlocksNonCommentEdit);

  const [deathInfo, setDeathInfo] = useState<string>(d?.deathInfo ?? '');
  const [comment, setComment] = useState<string>(d?.comment ?? '');
  const hf = d?.householdFields || {};
  const [nameField, setNameField] = useState<string>(hf['f52'] ?? '');
  const [zipField, setZipField] = useState<string>(hf['f53'] ?? '');
  const [addressField, setAddressField] = useState<string>(hf['f54'] ?? '');
  const [bankCode, setBankCode] = useState<string>(d?.bankCode ?? '');
  const [bankBranchCode, setBankBranchCode] = useState<string>(d?.bankBranchCode ?? '');
  const [bankAccountType, setBankAccountType] = useState<string>(
    bankAccountTypeLabelFromDb(d?.bankAccountType)
  );
  const [bankAccountNumber, setBankAccountNumber] = useState<string>(d?.bankAccountNumber ?? '');
  const [bankHolder, setBankHolder] = useState<string>(d?.bankAccountHolderKana ?? '');
  // 口座情報入力済みフラグ（5項目すべて非空かつ非空白で true）。保存時にサーバ側で再計算されるため
  // ペイロードには含めず、画面上の入力済みバッジ表示にのみ使用する。
  const bankInfoFilled = !!(
    bankCode.trim() &&
    bankBranchCode.trim() &&
    bankAccountType &&
    bankAccountNumber.trim() &&
    bankHolder.trim()
  );
  const [isPlaintiff, setIsPlaintiff] = useState<boolean>(Boolean(d?.isPlaintiff));
  const [requiresConsideration, setRequiresConsideration] = useState<boolean>(Boolean(d?.requiresConsideration));
  // 決定通知書教示文非表示（世帯単位チェックボックス）。header に明示値があればそれ、
  // 無ければ明細から初期判定（死亡者を除く明細が「すべて f56=1」のときだけ ON、1件でも 1以外なら OFF）。
  const [hideInstructionText, setHideInstructionText] = useState<boolean>(() =>
    d?.decisionNoticeHideInstructionText != null
      ? Boolean(d.decisionNoticeHideInstructionText)
      : computeHideInstructionTextInitial(normalizeDetailRows(d?.details || []), d?.deathInfo ?? '')
  );
  const [considerationAlertShown, setConsiderationAlertShown] = useState<boolean>(false);
  const [considerationAlertOpen, setConsiderationAlertOpen] = useState<boolean>(false);
  const [detailRows, setDetailRows] = useState<Record<string, string>[]>(() =>
    normalizeDetailRows(d?.details || [])
  );
  const [detailRowIds, setDetailRowIds] = useState<(number | null)[]>(() =>
    extractDetailRowIds(d?.details || [])
  );
  // 編集モードで「上に追加 / 下に追加」UI のために選択中の行 idx を保持。
  // No. セルクリックで toggle。selectedRowIdx は detailRows のインデックス（フィルタ前）。
  const [selectedRowIdx, setSelectedRowIdx] = useState<number | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  // 明細テーブルの全画面表示（74列を見渡すため。Esc / 背景クリックで解除）
  const [meisaiFullscreen, setMeisaiFullscreen] = useState(false);
  useEffect(() => {
    if (!meisaiFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMeisaiFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [meisaiFullscreen]);

  const memberIdentCodes = useMemo(
    () => new Set(detailRows.map(r => r.f13).filter(Boolean)),
    [detailRows]
  );

  const deathInfoUnknown = useMemo(() => {
    if (!deathInfo.trim()) return [];
    return deathInfo
      .split(',')
      .map(s => s.trim())
      .filter(s => s && !memberIdentCodes.has(s));
  }, [deathInfo, memberIdentCodes]);

  // 死亡として扱う世帯員(f13)集合（死亡フラグ f17 OR 死亡情報入力の和集合）。
  const deceasedMemberCodes = useMemo(
    () => computeDeceasedMemberCodes(detailRows, deathInfo),
    [detailRows, deathInfo]
  );
  // 表示する世帯主氏名。明細1行目が死亡している場合は死亡していない世帯員へ繰り上げる
  // （業務ルール: 死亡者は世帯主になれない。明細を上から順に探す）。
  const effectiveHeadName = effectiveHouseholdHeadName(detailRows, deceasedMemberCodes, d?.name);

  const visibleRowIndices = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .map(([k, v]) => [k, v.trim().toLowerCase()] as const)
      .filter(([, v]) => v !== '');
    if (activeFilters.length === 0) {
      return detailRows.map((_, i) => i);
    }
    return detailRows.reduce<number[]>((acc, row, idx) => {
      const ok = activeFilters.every(([key, needle]) => {
        const cell = (row[key] ?? '').toString().toLowerCase();
        return cell.includes(needle);
      });
      if (ok) acc.push(idx);
      return acc;
    }, []);
  }, [detailRows, columnFilters]);

  const hasActiveFilter = useMemo(
    () => Object.values(columnFilters).some(v => v.trim() !== ''),
    [columnFilters]
  );

  const updateColumnFilter = useCallback((fieldKey: string, value: string) => {
    setColumnFilters(prev => {
      if (value === '') {
        if (!(fieldKey in prev)) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      }
      return { ...prev, [fieldKey]: value };
    });
  }, []);

  const clearColumnFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  // 選択中の行が絞込で非表示になったら選択を解除する。
  // (選択→後からフィルタ入力でハイライト行が消えるのに「上/下に追加」ボタンが残り、
  //  見えない行を基準に挿入してしまう混乱を防ぐ。)
  useEffect(() => {
    if (selectedRowIdx != null && !visibleRowIndices.includes(selectedRowIdx)) {
      // 絞込で非表示になった選択行を解除する。
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRowIdx(null);
    }
  }, [visibleRowIndices, selectedRowIdx]);

  // 通知書出力済以降の家庭は原告チェックを true へ変更不可（仕様 4/24）。
  const currentStatusKey = caseStatusKeyByLabel(d?.paymentState);
  const noticeAlreadyPrinted = currentStatusKey != null
    && CASE_STATUS_CODE_MAP[currentStatusKey] >= CASE_STATUS_CODE_MAP.NOTICE_PRINTED;
  const plaintiffEditDisabled = !isNew && noticeAlreadyPrinted && !isPlaintiff;

  useEffect(() => {
    if (isNew) return;
    // 取得結果(URL の :recipientId と一致するもの)のみを一度だけ state へ反映する。
    // 親保持の data はフォールバックに使わない(stale な別レコード混入を防ぐため)。
    // fetchedData は getById 解決時に一度だけ変わるので、反映後にユーザー編集を上書きしない。
    if (!fetchedData || fetchedData.id !== recipientId) return;
    const src = fetchedData;
    // 取得した受給者データをフォーム各 state へ一度だけ反映する正当な初期化。
    /* eslint-disable react-hooks/set-state-in-effect */
    setDeathInfo(src?.deathInfo ?? '');
    setComment(src?.comment ?? '');
    const h = src?.householdFields || {};
    setNameField(h['f52'] ?? '');
    setZipField(h['f53'] ?? '');
    setAddressField(h['f54'] ?? '');
    setBankCode(src?.bankCode ?? '');
    setBankBranchCode(src?.bankBranchCode ?? '');
    setBankAccountType(bankAccountTypeLabelFromDb(src?.bankAccountType));
    setBankAccountNumber(src?.bankAccountNumber ?? '');
    setBankHolder(src?.bankAccountHolderKana ?? '');
    setIsPlaintiff(Boolean(src?.isPlaintiff));
    setRequiresConsideration(Boolean(src?.requiresConsideration));
    setHideInstructionText(
      src?.decisionNoticeHideInstructionText != null
        ? Boolean(src.decisionNoticeHideInstructionText)
        : computeHideInstructionTextInitial(normalizeDetailRows(src?.details || []), src?.deathInfo ?? '')
    );
    setDetailRows(normalizeDetailRows(src?.details || []));
    setDetailRowIds(extractDetailRowIds(src?.details || []));
    setSelectedRowIdx(null);
    setFormReady(true);
    // この端末に編集途中の下書きがあれば、まずは挂起してバナーで復元可否を選ばせる。
    // ここでは自動適用しない(サーバ値を勝手に上書きしないため)。
    const draft = loadDraft();
    if (draft) setPendingDraft(draft);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [fetchedData, isNew, recipientId, loadDraft]);

  useEffect(() => {
    if (isNew || considerationAlertShown) return;
    // URL の :recipientId と一致するデータでのみ判定する。
    // （前画面で開いていた別レコードの data が残っていると誤発火するため。）
    if (recipientId == null) return;
    const src = fetchedData?.id === recipientId
      ? fetchedData
      : data?.id === recipientId
        ? data
        : null;
    if (src && Boolean(src.requiresConsideration)) {
      // 要配慮データ検出時に一度だけ注意ダイアログを表示する。
      /* eslint-disable react-hooks/set-state-in-effect */
      setConsiderationAlertShown(true);
      setConsiderationAlertOpen(true);
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [data, fetchedData, isNew, considerationAlertShown, recipientId]);

  const updateDetailCell = useCallback((rowIdx: number, fieldKey: string, value: string) => {
    const normalized = fieldKey === 'f53' ? value.replace(/-/g, '') : value;
    setDetailRows(prev => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [fieldKey]: normalized };
      return next;
    });
  }, []);

  const addDetailRow = useCallback(() => {
    // 絞込中だと追加した空行はフィルタに一致せず画面に出ない(消えたように見える)ため、
    // 行追加時はフィルタを解除して必ず可視にする。
    setColumnFilters({});
    setDetailRows(prev => [...prev, {}]);
    setDetailRowIds(prev => [...prev, null]);
  }, []);

  // insertIdx の位置に空行を挿入。後続の行は 1 つ後ろにずれる。
  const insertDetailRowAt = useCallback((insertIdx: number) => {
    // 絞込中だと挿入空行が非表示になり、かつ可視リストと絶対indexがズレて挿入位置が
    // 直感に反するため、挿入時はフィルタを解除する。
    setColumnFilters({});
    setDetailRows(prev => {
      const next = [...prev];
      next.splice(insertIdx, 0, {});
      return next;
    });
    setDetailRowIds(prev => {
      const next = [...prev];
      next.splice(insertIdx, 0, null);
      return next;
    });
    // 挿入後は新しく挿入された行（= insertIdx）を選択中に切り替える。
    setSelectedRowIdx(insertIdx);
  }, []);

  const removeDetailRow = useCallback((rowIdx: number) => {
    setDetailRows(prev => prev.filter((_, i) => i !== rowIdx));
    setDetailRowIds(prev => prev.filter((_, i) => i !== rowIdx));
    setSelectedRowIdx(prev => {
      if (prev == null) return prev;
      if (prev === rowIdx) return null;
      // 削除行より後ろの選択は 1 つ前に詰める
      return prev > rowIdx ? prev - 1 : prev;
    });
  }, []);

  const toggleSelectedRow = useCallback((rowIdx: number) => {
    setSelectedRowIdx(prev => (prev === rowIdx ? null : rowIdx));
  }, []);

  // ── 明細セルの十字キー（矢印キー）ナビゲーション（No.35 操作体験改善）─────────
  // 単一行 input のため矢印キーは標準だとセル間移動できない（上下は無反応、左右はセル内
  // カーソル移動のみ）。Excel 風に上下=行移動、左右=キャレットが端にある時だけ列移動とする。
  // セルは data-cell-row（可視位置）/ data-cell-col（列 index）で参照する。
  const gridRef = useRef<HTMLTableElement | null>(null);
  const COL_COUNT = RECIPIENT_CSV_LABELS.length;

  const focusCell = useCallback((visPos: number, colIdx: number, caret: 'select' | 'start' | 'end'): boolean => {
    const root = gridRef.current;
    if (!root) return false;
    const el = root.querySelector<HTMLInputElement>(
      `input[data-cell-row="${visPos}"][data-cell-col="${colIdx}"]`,
    );
    if (!el) return false;
    el.focus();
    // フォーカス後にキャレット/選択を設定（type=text のみ対象なので setSelectionRange 可）。
    try {
      const len = el.value.length;
      if (caret === 'select') el.setSelectionRange(0, len);
      else if (caret === 'end') el.setSelectionRange(len, len);
      else el.setSelectionRange(0, 0);
    } catch {
      /* setSelectionRange 非対応の状況は無視 */
    }
    return true;
  }, []);

  const handleCellKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>, visPos: number, colIdx: number) => {
      // IME 変換中（日本語入力の確定前）は矢印キーを奪わない。候補選択を壊さないため必須。
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      // 修飾キー併用（Shift で範囲選択 / Ctrl・Alt・Meta のショートカット）はブラウザ標準に委ねる。
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

      const input = e.currentTarget;
      const rowCount = visibleRowIndices.length;

      switch (e.key) {
        case 'ArrowUp': {
          if (visPos <= 0) return;
          e.preventDefault();
          focusCell(visPos - 1, colIdx, 'select');
          break;
        }
        case 'ArrowDown': {
          if (visPos >= rowCount - 1) return;
          e.preventDefault();
          focusCell(visPos + 1, colIdx, 'select');
          break;
        }
        case 'ArrowLeft': {
          // キャレットがセル先頭（未選択）のときだけ左の列へ。途中ならセル内編集を優先。
          const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
          if (!atStart || colIdx <= 0) return;
          e.preventDefault();
          focusCell(visPos, colIdx - 1, 'end');
          break;
        }
        case 'ArrowRight': {
          // キャレットがセル末尾（未選択）のときだけ右の列へ。
          const len = input.value.length;
          const atEnd = input.selectionStart === len && input.selectionEnd === len;
          if (!atEnd || colIdx >= COL_COUNT - 1) return;
          e.preventDefault();
          focusCell(visPos, colIdx + 1, 'start');
          break;
        }
        default:
          return;
      }
    },
    [visibleRowIndices.length, COL_COUNT, focusCell],
  );

  const transferDateDisplay =
    d?.paymentState === '審査中'
      ? 'ー'
      : d?.transferDate != null && String(d.transferDate).trim() !== ''
        ? String(d.transferDate).trim()
        : 'ー';

  type FieldErrors = {
    name?: string;
    zip?: string;
    address?: string;
    bankCode?: string;
    bankBranchCode?: string;
    bankAccountNumber?: string;
    bankHolder?: string;
    detailRowCount?: string;
    /** key = `${rowIdx}-${fieldKey}` → 個別セルのエラー */
    detailCells: Record<string, string>;
    /** 明細サマリーパネル用のエントリ（共通 DetailErrorSummary に渡す） */
    detailEntries: DetailErrorEntry[];
  };

  const fieldErrors = useMemo<FieldErrors>(() => {
    const errs: FieldErrors = { detailCells: {}, detailEntries: [] };

    if (nameField.length > STRING_FIELD_MAX_LENGTH.f52) {
      errs.name = `${STRING_FIELD_MAX_LENGTH.f52}文字以内で入力してください`;
    } else if (!commentOnly && nameField.trim() !== '' && deceasedMemberCodes.has(nameField.trim())) {
      // 業務ルール: 死亡者は決定通知書宛名にできない（世帯主と同様）。保存をブロックする。
      // コメントのみ編集可ロールは宛名を修正できないため、既存データ起因のロックアウトを避けて対象外。
      errs.name = `宛名「${nameField.trim()}」は死亡された方として登録されています。死亡されていない方に変更してください`;
    }
    if (zipField.trim() !== '' && !/^\d{7}$/.test(zipField.trim())) {
      errs.zip = '7桁の半角数字（ハイフンなし、例 1600023）で入力してください';
    } else if (zipField.length > STRING_FIELD_MAX_LENGTH.f53) {
      errs.zip = `${STRING_FIELD_MAX_LENGTH.f53}文字以内で入力してください`;
    }
    if (addressField.length > STRING_FIELD_MAX_LENGTH.f54) {
      errs.address = `${STRING_FIELD_MAX_LENGTH.f54}文字以内で入力してください`;
    }

    if (isNew && detailRows.length === 0) {
      errs.detailRowCount = '1行以上追加してください';
    }
    const pushCellError = (rowIdx: number, key: string, label: string, message: string) => {
      const cellKey = `${rowIdx}-${key}`;
      if (errs.detailCells[cellKey]) return;
      errs.detailCells[cellKey] = message;
      errs.detailEntries.push({
        key: cellKey,
        lineNo: rowIdx + 1,
        label,
        message,
      });
    };

    if (isNew && detailRows.length > 0) {
      const head = detailRows[0] || {};
      const headHouseholdCode = (head.f1 ?? '').trim();
      if (headHouseholdCode === '') {
        pushCellError(0, 'f1', RECIPIENT_CSV_LABELS[0] || 'f1', '世帯番号として使用されるため必須です');
      }
    }

    detailRows.forEach((row, rowIdx) => {
      for (const [key, value] of Object.entries(row)) {
        if (!value || value.trim() === '') continue;
        const v = value.trim();
        const colIdx = parseInt(key.replace('f', ''), 10) - 1;
        const label = RECIPIENT_CSV_LABELS[colIdx] || key;
        let cellErr: string | null = null;
        if (SHORT_FIELDS.has(key) && !isValidShort(v)) {
          cellErr = `${SHORT_MIN}〜${SHORT_MAX} の整数を半角数字で入力してください`;
        } else if (LONG_FIELDS.has(key) && !isValidLong(v)) {
          cellErr = '整数を半角数字で入力してください';
        } else if (BOOLEAN_FIELDS.has(key) && !/^(true|false|0|1)$/i.test(v)) {
          cellErr = 'true/false または 0/1 を入力してください';
        } else if (key === 'f53' && !/^\d{7}$/.test(v)) {
          cellErr = '7桁の半角数字（ハイフンなし、例 1600023）で入力してください';
        } else {
          const max = STRING_FIELD_MAX_LENGTH[key];
          if (max != null && value.length > max) {
            cellErr = `${max}文字以内で入力してください`;
          }
        }
        if (cellErr) {
          pushCellError(rowIdx, key, label, cellErr);
        }
      }
    });

    // 世帯共通 8 項目の整合性チェック（共通ヘルパー）。
    // 基準はグリッド1行目の値。ユーザーが見ている値で比較する。
    for (const v of findHouseholdConsistencyViolations(detailRows)) {
      pushCellError(v.rowIdx, v.fieldKey, v.label, householdConsistencyMessage(v));
    }

    return errs;
  }, [nameField, zipField, addressField, detailRows, isNew, commentOnly, deceasedMemberCodes]);

  const [showSaveErrors, setShowSaveErrors] = useState(false);

  const collectBankFieldErrors = useCallback((): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (bankCode.trim() !== '') {
      const bankCodeErr = bankCodeError(bankCode);
    if (bankCodeErr) errs.bankCode = bankCodeErr.replace('。', '');
    }
    if (bankBranchCode.trim() !== '') {
      const bankBranchErr = bankBranchCodeError(bankBranchCode);
    if (bankBranchErr) errs.bankBranchCode = bankBranchErr.replace('。', '');
    }
    if (bankAccountNumber.trim() !== '') {
      const bankAccountNumberErr = bankAccountNumberError(bankAccountNumber);
    if (bankAccountNumberErr) errs.bankAccountNumber = bankAccountNumberErr.replace('。', '');
    }
    if (bankHolder.trim() !== '') {
      const bankHolderErr = bankAccountHolderKanaError(bankHolder);
    if (bankHolderErr) errs.bankHolder = bankHolderErr.replace('。', '');
    }
    return errs;
  }, [bankCode, bankBranchCode, bankAccountNumber, bankHolder]);

  const saveBankFieldErrors = showSaveErrors ? collectBankFieldErrors() : {};

  // 口座名義のリアルタイム検証。入力済みでフォーマット不正なら保存を待たず即赤字にする。
  // 空欄は「必須」エラー扱いにせず、グレーの案内のみを表示する（保存時に必須チェック）。
  const liveBankHolderError =
    bankHolder.trim() !== '' ? bankAccountHolderKanaError(bankHolder).replace('。', '') : '';

  const validate = (bankErrs: Record<string, string> = saveBankFieldErrors): string[] => {
    const list: string[] = [];
    if (fieldErrors.name) list.push(`${BASIC_INFO_FIELD_LABELS.addressee}: ${fieldErrors.name}`);
    if (fieldErrors.zip) list.push(`${BASIC_INFO_FIELD_LABELS.postalCode}: ${fieldErrors.zip}`);
    if (fieldErrors.address) list.push(`${BASIC_INFO_FIELD_LABELS.address}: ${fieldErrors.address}`);
    if (bankErrs.bankCode) list.push(`銀行番号: ${bankErrs.bankCode}`);
    if (bankErrs.bankBranchCode) list.push(`支店番号: ${bankErrs.bankBranchCode}`);
    if (bankErrs.bankAccountNumber) list.push(`口座番号: ${bankErrs.bankAccountNumber}`);
    if (bankErrs.bankHolder) list.push(`口座名義: ${bankErrs.bankHolder}`);
    if (fieldErrors.detailRowCount) list.push(`明細: ${fieldErrors.detailRowCount}`);
    for (const entry of fieldErrors.detailEntries) {
      list.push(`明細 ${entry.lineNo}行目「${entry.label}」: ${entry.message}`);
    }
    return list;
  };

  // 決定通知書教示文非表示の読込時ベースライン（header 明示値があればそれ、無ければ明細から初期判定）。
  // 編集中の hideInstructionText と比較して変更検知に使う。
  const initialHideInstructionText: boolean =
    (d as { decisionNoticeHideInstructionText?: boolean | null })?.decisionNoticeHideInstructionText != null
      ? Boolean((d as { decisionNoticeHideInstructionText?: boolean | null }).decisionNoticeHideInstructionText)
      : computeHideInstructionTextInitial(
          normalizeDetailRows((d as { details?: Parameters<typeof normalizeDetailRows>[0] })?.details || []),
          (d as { deathInfo?: string | null })?.deathInfo ?? '',
        );

  // コメント以外のフィールドが初期値から変更されているか。
  // false の場合はコメントのみ送信し、バックエンドのステータス巻き戻しを発生させない。
  const hasNonCommentChanges: boolean = !isNew && (() => {
    const initHf = (d as { householdFields?: Record<string, string> })?.householdFields ?? {};
    return (
      deathInfo.trim() !== ((d as { deathInfo?: string | null })?.deathInfo ?? '').trim() ||
      nameField !== (initHf['f52'] ?? '') ||
      zipField !== (initHf['f53'] ?? '') ||
      addressField !== (initHf['f54'] ?? '') ||
      bankCode !== ((d as { bankCode?: string })?.bankCode ?? '') ||
      bankBranchCode !== ((d as { bankBranchCode?: string })?.bankBranchCode ?? '') ||
      bankAccountType !== bankAccountTypeLabelFromDb((d as { bankAccountType?: number | null })?.bankAccountType) ||
      bankAccountNumber !== ((d as { bankAccountNumber?: string })?.bankAccountNumber ?? '') ||
      bankHolder !== ((d as { bankAccountHolderKana?: string })?.bankAccountHolderKana ?? '') ||
      isPlaintiff !== Boolean((d as { isPlaintiff?: boolean })?.isPlaintiff) ||
      requiresConsideration !== Boolean((d as { requiresConsideration?: boolean })?.requiresConsideration) ||
      hideInstructionText !== initialHideInstructionText ||
      JSON.stringify(detailRows) !== JSON.stringify(normalizeDetailRows((d as { details?: Parameters<typeof normalizeDetailRows>[0] })?.details))
    );
  })();

  // ステータス巻き戻し確認モーダルの判定。
  // バックエンド RecipientService.requestChangesRecalcAffectingFields と同じ項目集合に揃える
  // （給付額・決定通知書に影響する項目のみ）。コメント・口座情報・要配慮フラグは巻き戻し対象外なので、
  // これらだけの編集では確認モーダルを出さない（例: 給付決定通知書ダウンロード済で要配慮のみ更新）。
  const hasRecalcAffectingChanges: boolean = !isNew && (() => {
    const initHf = (d as { householdFields?: Record<string, string> })?.householdFields ?? {};
    return (
      deathInfo.trim() !== ((d as { deathInfo?: string | null })?.deathInfo ?? '').trim() ||
      nameField !== (initHf['f52'] ?? '') ||
      zipField !== (initHf['f53'] ?? '') ||
      addressField !== (initHf['f54'] ?? '') ||
      isPlaintiff !== Boolean((d as { isPlaintiff?: boolean })?.isPlaintiff) ||
      hideInstructionText !== initialHideInstructionText ||
      JSON.stringify(detailRows) !== JSON.stringify(normalizeDetailRows((d as { details?: Parameters<typeof normalizeDetailRows>[0] })?.details))
    );
  })();

  // 現在の編集 state を下書きペイロードへまとめる(自動保存・手動「一時保存」で共用)。
  const collectDraftPayload = useCallback(() => ({
    comment, deathInfo, nameField, zipField, addressField,
    bankCode, bankBranchCode, bankAccountType, bankAccountNumber, bankHolder,
    isPlaintiff, requiresConsideration, hideInstructionText, detailRows, detailRowIds,
  }), [
    comment, deathInfo, nameField, zipField, addressField,
    bankCode, bankBranchCode, bankAccountType, bankAccountNumber, bankHolder,
    isPlaintiff, requiresConsideration, hideInstructionText, detailRows, detailRowIds,
  ]);

  // 編集モード: サーバ値から少しでも変化していれば下書き対象とみなす(無変更なら空の下書きを作らない)。
  const commentChanged = !isNew && comment !== ((d as { comment?: string })?.comment ?? '');
  // 新規モード: 比較すべきサーバ値が無いため、初回レンダ時(空フォーム)の payload を基準として
  // 一度だけ確定し(useState 遅延初期化)、それと異なれば下書き対象とする。
  const currentDraftJson = isNew ? JSON.stringify(collectDraftPayload()) : '';
  const [newModeBaseline] = useState(currentDraftJson);
  const newModeDirty = isNew && currentDraftJson !== newModeBaseline;
  const draftDirty = isNew ? newModeDirty : (hasNonCommentChanges || commentChanged);

  // 編集開始時に読み込んだサーバ側レコードの最終更新日時。下書きに同梱して、復元/保存時に
  // 「編集中に他の利用者が更新していないか」を判定する基準にする(新規は対応レコードが無く null)。
  const serverBaselineUpdatedAt = isNew ? null : (fetchedData?.updatedAt ?? null);

  // 自動一時保存。入力が止まってから約1秒後にこの端末へ下書きを書き出す(新規・編集とも)。
  // 復元バナー表示中(pendingDraft)は、これから提示する下書きを上書きしないよう保存しない。
  useEffect(() => {
    if (!formReady || pendingDraft || !draftDirty) return;
    const timer = setTimeout(() => {
      const savedAt = saveDraft(collectDraftPayload(), serverBaselineUpdatedAt);
      if (savedAt) setDraftSavedAt(savedAt);
    }, 1000);
    return () => clearTimeout(timer);
  }, [formReady, pendingDraft, draftDirty, collectDraftPayload, saveDraft, serverBaselineUpdatedAt]);

  // 下書きを現在のフォームへ適用する(読込時 effect と同じ要領で各 state を更新)。
  const applyDraft = (draft: RecipientDraft) => {
    setComment(draft.comment);
    setDeathInfo(draft.deathInfo);
    setNameField(draft.nameField);
    setZipField(draft.zipField);
    setAddressField(draft.addressField);
    setBankCode(draft.bankCode);
    setBankBranchCode(draft.bankBranchCode);
    setBankAccountType(draft.bankAccountType);
    setBankAccountNumber(draft.bankAccountNumber);
    setBankHolder(draft.bankHolder);
    setIsPlaintiff(draft.isPlaintiff);
    setRequiresConsideration(draft.requiresConsideration);
    setHideInstructionText(draft.hideInstructionText);
    setDetailRows(draft.detailRows);
    setDetailRowIds(draft.detailRowIds);
    setSelectedRowIdx(null);
    setDraftSavedAt(draft.savedAt);
    setPendingDraft(null);
    toast.success('下書きを復元しました');
  };

  // 復元バナーで「破棄」: 下書きを消し、サーバ値の表示のまま続行する(編集モード)。
  const handleDiscardDraft = () => {
    clearDraft();
    setPendingDraft(null);
    setDraftSavedAt(null);
    toast.info('下書きを破棄しました');
  };

  // 新規モード: 一覧の下書きを復元する。アクティブスロットを当該 scope へ切替え、
  // 以後の自動保存もそのスロットへ書き戻す(別スロットに増殖させない)。一覧からは取り除く。
  const handleRestoreNewDraft = (entry: NewDraftEntry) => {
    applyDraft(entry.draft);
    setActiveDraftScope(entry.scope);
    setOtherNewDrafts(prev => prev.filter(e => e.scope !== entry.scope));
  };

  // 新規モード: 一覧の下書きを個別に破棄する。
  const handleDiscardNewDraft = (entry: NewDraftEntry) => {
    removeDraftScope(entry.scope);
    setOtherNewDrafts(prev => prev.filter(e => e.scope !== entry.scope));
    toast.info('下書きを破棄しました');
  };

  // 編集モードの復元。下書き作成時点と現在のサーバ最終更新日時が食い違う場合
  // (= 編集中に他の利用者がこの受給者を更新)は、上書きの可否をユーザに確認してから適用する。
  const handleRestoreEditDraft = () => {
    if (!pendingDraft) return;
    const draftBaseline = pendingDraft.serverUpdatedAt ?? null;
    const currentServer = fetchedData?.updatedAt ?? null;
    if (draftBaseline && currentServer && draftBaseline !== currentServer) {
      Modal.confirm({
        title: 'サーバ側のデータが更新されています',
        content: `この下書きを保存した後に、他の利用者がこの受給者のデータを更新しています(サーバ最終更新: ${formatDateTime(currentServer)})。\n復元すると相手の変更が画面上で上書きされます(この時点ではまだ保存されません)。復元しますか?`,
        okText: '復元する',
        cancelText: 'やめる',
        onOk: () => applyDraft(pendingDraft),
      });
      return;
    }
    applyDraft(pendingDraft);
  };

  // 復元バナーに出す下書き一覧。編集モード(pendingDraft の単一)と新規モード(複数)を
  // 同一の見た目・文言で表示するため、共通配列にまとめる。
  const draftBanners: { key: string; savedAt: string; onRestore: () => void; onDiscard: () => void }[] =
    isNew
      ? otherNewDrafts.map(entry => ({
          key: entry.scope,
          savedAt: entry.savedAt,
          onRestore: () => handleRestoreNewDraft(entry),
          onDiscard: () => handleDiscardNewDraft(entry),
        }))
      : pendingDraft
        ? [{
            key: 'edit',
            savedAt: pendingDraft.savedAt,
            onRestore: handleRestoreEditDraft,
            onDiscard: handleDiscardDraft,
          }]
        : [];

  // 手動「一時保存」ボタン。即座にこの端末へ下書きを保存する。
  const handleManualDraftSave = () => {
    const savedAt = saveDraft(collectDraftPayload(), serverBaselineUpdatedAt);
    if (savedAt) {
      setDraftSavedAt(savedAt);
      toast.success('下書きを一時保存しました');
    } else {
      toast.warning('ローカルに保存されている下書きが上限数に達しているため、保存できません。');
    }
  };

  const doSave = async () => {
    if (savingRef.current) return; // 二重送信ガード(連打で create/update が複数回飛ぶのを防ぐ)
    savingRef.current = true;
    setSaving(true);
    try {
      const bankAccountTypeValue = bankAccountTypeToDb(bankAccountType);
      if (isNew) {
        // 新規モードでは入力された世帯属性（決定通知書宛名/決定通知書郵便番号/決定通知書住所）のみを送信する。
        // 未入力の項目はバック側で明細1行目から複写される（CSV 取込と同じ動作）。
        const overrideHouseholdFields: Record<string, string> = {};
        if (nameField.trim() !== '') overrideHouseholdFields.f52 = nameField;
        if (zipField.trim() !== '') overrideHouseholdFields.f53 = zipField;
        if (addressField.trim() !== '') overrideHouseholdFields.f54 = addressField;
        await recipientsApi.create({
          deathInfo: deathInfo.trim() || null,
          comment,
          bankCode,
          bankBranchCode,
          bankAccountType: bankAccountTypeValue,
          bankAccountNumber,
          bankAccountHolderKana: bankHolder,
          isPlaintiff,
          requiresConsideration,
          decisionNoticeHideInstructionText: hideInstructionText,
          householdFields: Object.keys(overrideHouseholdFields).length > 0 ? overrideHouseholdFields : undefined,
          details: detailRows,
        }, { _silent: true });
        // 登録できたので、この端末の新規用下書きは不要(次回 new で古い下書きの復元を促さない)。
        clearDraft();
        toast.success('登録しました');
        navigate('ocr-read-list');
        return;
      }
      // 編集モード：決定通知書宛名/郵便番号/住所（f52/f53/f54）は header(recipients) を真値とし、
      // textbox の値を householdFields としてのみ送る。明細(detailRows)はグリッドの値のまま送り、
      // 通知書系の値を明細1行目へ書き戻さない（IJ_00013: 世帯主氏名 ≠ 決定通知書宛名）。
      const editedDetailRows = detailRows;
      const nextHouseholdFields = buildEditHouseholdFields(editedDetailRows[0], {
        f52: nameField,
        f53: zipField,
        f54: addressField,
      });
      let updatedResponse: RecipientResponse | null = null;
      if (d?.id) {
        const nonRecalcPayload: RecipientUpdateRequest = {
          comment,
          bankCode,
          bankBranchCode,
          bankAccountType: bankAccountTypeValue,
          bankAccountNumber,
          bankAccountHolderKana: bankHolder,
          requiresConsideration,
        };
        const recalcPayload: RecipientUpdateRequest = {
          ...nonRecalcPayload,
          deathInfo: deathInfo.trim() || null,
          isPlaintiff,
          decisionNoticeHideInstructionText: hideInstructionText,
          householdFields: nextHouseholdFields,
          // detailRowIds[i] が null の行は後端で新規 detail として作成される。
          // 受信したリストから漏れた既存 id の detail は後端で削除される。
          details: editedDetailRows.map((fields, i) => ({ id: detailRowIds[i] ?? null, fields })),
        };
        // 口座情報・要配慮・コメントだけの変更では details/householdFields を送らない。
        // 後端は details != null を再計算対象と判定するため、送ると status が不要に巻き戻る。
        const updatePayload = (commentOnly || !hasNonCommentChanges)
          ? { comment }
          : hasRecalcAffectingChanges
            ? recalcPayload
            : nonRecalcPayload;
        const res = await recipientsApi.update(d.id, updatePayload, { _silent: true });
        updatedResponse = res.data;
      }
      flushSync(() => {
        // レスポンスを優先（detail の新採番 id を反映するため）。コメントのみ送信時はレスポンス未取得でも詳細項目に変化はない。
        setData(updatedResponse ?? {
          ...d,
          deathInfo: deathInfo.trim() || null,
          comment,
          bankCode,
          bankBranchCode,
          bankAccountType: bankAccountTypeValue,
          bankAccountNumber,
          bankAccountHolderKana: bankHolder,
          isPlaintiff,
          requiresConsideration,
          decisionNoticeHideInstructionText: hideInstructionText,
          householdFields: nextHouseholdFields,
          details: editedDetailRows.map((fields, i) => ({ id: detailRowIds[i] ?? null, fields })) as RecipientDetailResponse[],
        });
      });
      // 本保存が成功したので、この端末の下書きは不要。残すと次回開いた時に古い下書きの復元を促してしまう。
      clearDraft();
      toast.success('保存しました');
      navigate('recipient-detail', detailOpts);
    } catch (e) {
      if (isHandledAuthError(e)) return;
      // create/update は _silent で送っているため interceptor はトーストを出さない。
      // ここで明示的に失敗を通知する(サーバ側 fieldErrors があればその先頭メッセージ、無ければ汎用文言)。
      Modal.error({ content: getErrorMessage(e, '保存に失敗しました。時間をおいて再度お試しください。') });
    } finally {
      savingRef.current = false;
      setSaving(false);
      setShowResetConfirm(false);
    }
  };

  // 保存直前に最新のサーバ最終更新日時を取得し、編集開始時(fetchedData.updatedAt)と食い違う
  // (= 編集中に他の利用者が更新)場合は、上書きの可否をユーザに確認する。
  // 続行(=保存)なら true、キャンセルなら false。取得失敗時は保存をブロックしない。
  const confirmOverwriteIfServerChanged = async (): Promise<boolean> => {
    if (isNew || !d?.id) return true;
    const baseline = fetchedData?.updatedAt ?? null;
    if (!baseline) return true;
    let latest: string | null = null;
    try {
      const res = await recipientsApi.getById(d.id);
      latest = res.data?.updatedAt ?? null;
    } catch {
      return true;
    }
    if (!latest || latest === baseline) return true;
    return await new Promise<boolean>(resolve => {
      Modal.confirm({
        title: '他の利用者がこのデータを更新しています',
        content: `編集を開始した後に、別の利用者がこの受給者のデータを更新しました(サーバ最終更新: ${formatDateTime(latest!)})。\nこのまま保存すると相手の変更が上書きされます。保存しますか?`,
        okText: '保存する',
        cancelText: 'やめる',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const save = async () => {
    setShowSaveErrors(true);
    const bankErrs = collectBankFieldErrors();
    const errors = validate(bankErrs);
    if (errors.length > 0) {
      Modal.error({ content: errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n…他 ${errors.length - 5} 件` : '') });
      return;
    }
    // 競合検出: 編集中に他の利用者が更新していたら上書き可否を確認(キャンセルなら中断)。
    if (!(await confirmOverwriteIfServerChanged())) return;
    if (!isNew && hasRecalcAffectingChanges && statusResetsOnEdit(d?.paymentState)) {
      setShowResetConfirm(true);
      return;
    }
    await doSave();
  };

  // 編集モード: 取得失敗 / 取得完了前は専用画面を出す。stale な別レコードを表示・編集・保存させない。
  if (!isNew && loadError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
          <p className="text-sm text-gray-700 mb-4">受給者情報の読み込みに失敗しました。時間をおいて再度お試しください。</p>
          <button type="button" onClick={() => navigate(baseCrumb.page || 'recipient-search')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition cursor-pointer">一覧へ戻る</button>
        </div>
      </div>
    );
  }
  if (!isNew && !formReady) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6 text-sm text-gray-500">
        読み込み中…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={isNew
          ? [
              { label: baseCrumb.label, page: baseCrumb.page },
              { label: '新規受給者登録' },
            ]
          : [
              { label: baseCrumb.label, page: baseCrumb.page },
              { label: returnDetailPageTitle, page: 'recipient-detail', navigateOpts: detailOpts },
              { label: '編集' },
            ]
        }
      />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <BackButton onClick={() => navigate(isNew ? (baseCrumb.page || 'recipient-search') : 'recipient-detail', isNew ? undefined : detailOpts)} />

        {isNew ? (
          <p className="text-sm text-gray-600 mb-4">
            識別IDは登録時に自動採番されます。世帯番号は明細1行目の世帯識別符号が使用されます。
            基本情報の決定通知書宛名・決定通知書郵便番号（ハイフンなし）・決定通知書住所を入力すると、明細1行目の値より優先されます。
          </p>
        ) : (
          <p className="text-sm text-gray-600 mb-4">
            識別ID: <span className="font-mono font-medium text-gray-900">{d?.identId}</span>
          </p>
        )}

        {statusBlocksNonCommentEdit && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {`現在の案件ステータス「${d?.paymentState ?? ''}」ではコメントのみ編集可能です。その他の項目は閲覧のみとなります。`}
          </div>
        )}

        {/* 一時保存(下書き)の復元バナー。編集・新規で同一の見た目・文言。
            サーバ値/初期フォームを勝手に上書きせず、各下書きを「復元 / 破棄」で選ばせる。 */}
        {draftBanners.length > 0 && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm font-bold text-amber-900">
              {draftBanners.length === 1
                ? '入力途中の下書きが保存されています'
                : `入力途中の下書きが${draftBanners.length}件保存されています`}
            </p>
            <p className="mt-0.5 mb-2 text-xs text-amber-700">
              この端末に保存された下書きです。復元すると続きから入力できます。
            </p>
            <ul className="divide-y divide-amber-200 border-t border-amber-200">
              {draftBanners.map(b => (
                <li key={b.key} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-sm text-amber-900">
                    保存日時: {formatDateTime(b.savedAt)}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <button type="button" onClick={b.onRestore}
                      className="px-4 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition shadow-sm cursor-pointer">
                      復元
                    </button>
                    <button type="button" onClick={b.onDiscard}
                      className="px-4 py-1.5 text-sm border border-amber-300 text-amber-800 bg-white rounded-lg hover:bg-amber-100 transition cursor-pointer">
                      破棄
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* コメント */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-gray-800 mb-3">コメント</h3>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            placeholder="コメントを入力してください"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* 死亡情報入力 */}
        <fieldset disabled={commentOnly} className="contents">
        <div className={`bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6${commentOnly ? ' opacity-60' : ''}`}>
          <h3 className="text-sm font-bold text-gray-800 mb-1">死亡情報入力</h3>
          <p className="text-xs text-gray-400 mb-3">世帯員識別符号をカンマ区切りで入力（複数可）</p>
          <input
            type="text"
            value={deathInfo}
            onChange={(e) => setDeathInfo(e.target.value)}
            placeholder="例: 社会　一郎"
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:border-transparent ${deathInfoUnknown.length > 0 ? 'border-amber-400 focus:ring-amber-400' : 'border-gray-300 focus:ring-blue-500'}`}
          />
          {deathInfoUnknown.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              以下の識別符号はこの世帯に存在しません: {deathInfoUnknown.join('、')}
            </p>
          )}
        </div>
        </fieldset>

        <fieldset disabled={commentOnly} className="contents">
        {/* 基本情報 */}
        <div className={`bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6${commentOnly ? ' opacity-60' : ''}`}>
          <h2 className="text-xl font-bold text-gray-800 mb-5 pb-3 border-b border-gray-200">基本情報</h2>
          <div className="space-y-5 max-w-3xl">
            {!isNew ? (
              <div>
                <p className="block text-sm font-medium text-gray-500 mb-1">{BASIC_INFO_FIELD_LABELS.headName}</p>
                <p className="text-sm text-gray-900">
                  {effectiveHeadName !== '' ? effectiveHeadName : '—'}
                </p>
              </div>
            ) : null}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{BASIC_INFO_FIELD_LABELS.addressee}</label>
              <input type="text" value={nameField} onChange={e => setNameField(e.target.value)}
                placeholder={isNew ? '未入力の場合は明細1行目の値を使用' : ''}
                aria-invalid={!!fieldErrors.name}
                className={`w-full border rounded-lg px-4 py-2 text-sm text-gray-900 focus:ring-2 focus:border-transparent ${fieldErrors.name ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'}`} />
              {fieldErrors.name ? <p className="mt-1 text-xs text-red-600">・{BASIC_INFO_FIELD_LABELS.addressee}: {fieldErrors.name}</p> : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{BASIC_INFO_FIELD_LABELS.postalCode}</label>
              <input type="text" value={zipField} onChange={e => setZipField(e.target.value.replace(/-/g, ''))}
                placeholder={isNew ? '未入力の場合は明細1行目の値を使用' : ''}
                aria-invalid={!!fieldErrors.zip}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-900 font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              {fieldErrors.zip ? <p className="mt-1 text-xs text-red-600">・{BASIC_INFO_FIELD_LABELS.postalCode}: {fieldErrors.zip}</p> : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{BASIC_INFO_FIELD_LABELS.address}</label>
              <input type="text" value={addressField} onChange={e => setAddressField(e.target.value)}
                placeholder={isNew ? '未入力の場合は明細1行目の値を使用' : ''}
                aria-invalid={!!fieldErrors.address}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              {fieldErrors.address ? <p className="mt-1 text-xs text-red-600">・{BASIC_INFO_FIELD_LABELS.address}: {fieldErrors.address}</p> : null}
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-gray-700">
                <input
                  type="checkbox"
                  checked={hideInstructionText}
                  onChange={(e) => setHideInstructionText(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="font-medium">決定通知書教示文非表示</span>
                <span className="text-xs text-gray-500">（チェックすると決定通知書の教示文を非表示にします）</span>
              </label>
            </div>
            <div>
              <label className={`inline-flex items-center gap-2 text-sm ${plaintiffEditDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700'}`}
                title={plaintiffEditDisabled ? '通知書出力済以降は原告に変更できません' : undefined}>
                <input
                  type="checkbox"
                  checked={isPlaintiff}
                  disabled={plaintiffEditDisabled}
                  onChange={(e) => setIsPlaintiff(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <span className="font-medium">原告</span>
                <span className="text-xs text-gray-500">（チェックすると決定通知書及び振込データの出力対象外になります）</span>
              </label>
              {plaintiffEditDisabled ? (
                <p className="mt-1 text-xs text-amber-600">※決定通知書出力済の案件は原告へ変更できません。</p>
              ) : null}
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-gray-700">
                <input
                  type="checkbox"
                  checked={requiresConsideration}
                  onChange={(e) => setRequiresConsideration(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="font-medium">要配慮</span>
                <span className="text-xs text-gray-500">（チェックすると詳細／編集画面を開く際に注意ポップアップを表示します）</span>
              </label>
            </div>
            {isNew ? null : (
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">振込予定日</div>
                <p className="mt-1 text-sm text-gray-900">{transferDateDisplay}</p>
              </div>
            )}

            {/* 口座情報 */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <span>口座情報</span>
                {bankInfoFilled ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    ✓ 入力済み
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    未入力
                  </span>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">銀行番号</label>
                    <input type="text" value={bankCode} onChange={e => setBankCode(sanitizeBankCodeInput(e.target.value))}
                      inputMode="numeric"
                      maxLength={4}
                      aria-invalid={!!saveBankFieldErrors.bankCode}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-mono bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    {saveBankFieldErrors.bankCode ? <p className="mt-1 text-xs text-red-600">・銀行番号: {saveBankFieldErrors.bankCode}</p> : null}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">支店番号</label>
                    <input type="text" value={bankBranchCode} onChange={e => setBankBranchCode(sanitizeBankBranchCodeInput(e.target.value))}
                      inputMode="numeric"
                      maxLength={3}
                      aria-invalid={!!saveBankFieldErrors.bankBranchCode}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-mono bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    {saveBankFieldErrors.bankBranchCode ? <p className="mt-1 text-xs text-red-600">・支店番号: {saveBankFieldErrors.bankBranchCode}</p> : null}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">預金種目</label>
                    <DemoSelect value={bankAccountType} onChange={e => setBankAccountType(e.target.value)}
                      className="w-full pl-3 pr-9 text-gray-900">
                      <option value="">選択してください</option>
                      {APPLICATION_BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </DemoSelect>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">口座番号</label>
                    <input type="text" value={bankAccountNumber} onChange={e => setBankAccountNumber(sanitizeBankAccountNumberInput(e.target.value))}
                      inputMode="numeric"
                      maxLength={7}
                      aria-invalid={!!saveBankFieldErrors.bankAccountNumber}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-mono bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    {saveBankFieldErrors.bankAccountNumber ? <p className="mt-1 text-xs text-red-600">・口座番号: {saveBankFieldErrors.bankAccountNumber}</p> : null}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">口座名義</label>
                    <input type="text" value={bankHolder}
                      onChange={e => setBankHolder((e.nativeEvent as { isComposing?: boolean }).isComposing ? e.target.value : sanitizeBankAccountHolderInput(e.target.value))}
                      onCompositionEnd={e => setBankHolder(sanitizeBankAccountHolderInput(e.currentTarget.value))}
                      aria-invalid={!!(saveBankFieldErrors.bankHolder || liveBankHolderError)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    {saveBankFieldErrors.bankHolder || liveBankHolderError ? (
                      <p className="mt-1 text-xs text-red-600">・口座名義: {saveBankFieldErrors.bankHolder || liveBankHolderError}</p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">半角ｶﾀｶﾅ、半角スペースのみで入力してください</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 明細 */}
        {meisaiFullscreen ? (
          <div className="fixed inset-0 z-[55] bg-black/40" aria-hidden onClick={() => setMeisaiFullscreen(false)} />
        ) : null}
        <div className={
          meisaiFullscreen
            ? `fixed inset-2 sm:inset-4 z-[60] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden${commentOnly ? ' opacity-60' : ''}`
            : `bg-white rounded-xl shadow-lg border border-gray-200 mb-6 overflow-hidden${commentOnly ? ' opacity-60' : ''}`
        }>
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-lg font-bold text-gray-800">明細{isNew ? '（世帯員）' : ''}</h3>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">
                <span className="font-mono font-medium text-gray-700">{RECIPIENT_CSV_LABELS.length}</span>
                項目 × {hasActiveFilter ? (
                  <>
                    表示
                    <span className="font-mono font-medium text-gray-700">{visibleRowIndices.length}</span>
                    /全
                    <span className="font-mono font-medium text-gray-700">{detailRows.length}</span>
                  </>
                ) : (
                  <>
                    全
                    <span className="font-mono font-medium text-gray-700">{detailRows.length}</span>
                  </>
                )}
                件{isNew
                  ? '（1行目が世帯主）'
                  : (selectedRowIdx != null
                      ? `（${selectedRowIdx + 1}行目選択中：上下に行を挿入できます）`
                      : '（No. セルをクリックすると、その上下に行を挿入できます）')}
              </p>
              {!commentOnly && detailRows.length > 0 ? (
                <span
                  title="セルにカーソルがある状態で ↑↓ で行移動、行頭/行末で ←→ すると列移動できます"
                  className="hidden sm:inline-flex items-center shrink-0 whitespace-nowrap gap-1 text-[11px] text-gray-400 select-none"
                >
                  <span aria-hidden className="font-mono text-gray-500">↑↓←→</span>
                  でセル移動
                </span>
              ) : null}
              {hasActiveFilter ? (
                <button
                  type="button"
                  onClick={clearColumnFilters}
                  title="すべてのフィルタを解除"
                  className="inline-flex items-center shrink-0 whitespace-nowrap gap-1.5 h-8 px-3 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full shadow-sm hover:text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition cursor-pointer"
                >
                  <CloseCircleFilled className="text-[11px]" />
                  <span>フィルタクリア</span>
                </button>
              ) : null}
              {!isNew && selectedRowIdx != null ? (
                <>
                  <button type="button" onClick={() => insertDetailRowAt(selectedRowIdx)}
                    title={`${selectedRowIdx + 1}行目の上に新しい行を挿入`}
                    className="inline-flex items-center shrink-0 whitespace-nowrap h-8 px-3 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm cursor-pointer">
                    ↑ 上に追加
                  </button>
                  <button type="button" onClick={() => insertDetailRowAt(selectedRowIdx + 1)}
                    title={`${selectedRowIdx + 1}行目の下に新しい行を挿入`}
                    className="inline-flex items-center shrink-0 whitespace-nowrap h-8 px-3 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm cursor-pointer">
                    ↓ 下に追加
                  </button>
                </>
              ) : null}
              <button type="button" onClick={addDetailRow}
                className="inline-flex items-center shrink-0 whitespace-nowrap h-8 px-3 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-sm cursor-pointer">
                + 末尾に追加
              </button>
              {/* 全画面トグル: commentOnly(fieldset disabled)でも閲覧のため使えるよう button ではなく div で実装 */}
              <div role="button" tabIndex={0}
                onClick={() => setMeisaiFullscreen(v => !v)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMeisaiFullscreen(v => !v); } }}
                title={meisaiFullscreen ? '全画面を解除（Esc）' : '全画面で表示'}
                className="inline-flex items-center shrink-0 whitespace-nowrap gap-1.5 h-8 px-3 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition cursor-pointer select-none">
                {meisaiFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                <span>{meisaiFullscreen ? '全画面解除' : '全画面'}</span>
              </div>
            </div>
          </div>
          {detailRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {isNew ? '「+ 末尾に追加」で世帯主の明細を追加してください' : '明細がありません。「+ 末尾に追加」で行を追加できます'}
              {fieldErrors.detailRowCount ? (
                <p className="mt-2 text-xs text-red-600">明細: {fieldErrors.detailRowCount}</p>
              ) : null}
            </div>
          ) : (
            <div
              className={`meisai-scroll-x ${meisaiFullscreen ? 'flex-1 min-h-0' : 'max-h-[min(70vh,36rem)]'} overflow-auto overflow-y-scroll overscroll-contain [scrollbar-gutter:stable]`}
              onScroll={(e) => { const el = e.currentTarget; const v = el.scrollLeft > 0 ? '1' : '0'; if (el.getAttribute('data-scrolledx') !== v) el.setAttribute('data-scrolledx', v); }}
            >
              <table ref={gridRef} className="min-w-max text-xs border-separate border-spacing-0">
                <colgroup>
                  <col className="w-12 min-w-[3rem]" />
                  {RECIPIENT_CSV_LABELS.map((_: string, colIdx: number) => (
                    <col key={`cg-${colIdx}`} className="min-w-[6.5rem] w-[6.5rem]" />
                  ))}
                  <col className="w-16 min-w-[4rem]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="sticky top-0 left-0 z-30 box-border text-left font-semibold text-gray-800 px-2 py-2 bg-gray-200 border-r border-gray-300 w-12 min-w-[3rem]">No.</th>
                    {RECIPIENT_CSV_LABELS.map((label: string, colIdx: number) => {
                      const frozen = isFrozenDataCol(colIdx);
                      const lastFrozen = isLastFrozenDataCol(colIdx);
                      return (
                        <th key={`h-${colIdx}`} title={label}
                          className={`sticky top-0 ${frozen ? `${FROZEN_LEFT_CLASS[colIdx]} z-30` : 'z-20'} box-border text-left font-semibold text-gray-700 px-2 py-2 bg-gray-100 border-b border-gray-200 whitespace-nowrap min-w-[6.5rem] w-[6.5rem] truncate ${lastFrozen ? 'border-r border-transparent meisai-freeze-edge' : 'shadow-[0_1px_0_0_rgb(229,231,235)]'}`}
                        >{label}</th>
                      );
                    })}
                    <th className="sticky top-0 z-20 box-border text-center font-semibold text-gray-700 px-2 py-2 bg-gray-100 border-b border-gray-200 whitespace-nowrap w-16 min-w-[4rem]">操作</th>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th className="sticky top-[34px] left-0 z-30 box-border px-2 py-1 bg-gray-100 border-r border-b border-gray-300 w-12 min-w-[3rem]">
                      <span className="text-[10px] font-medium text-gray-500">絞込</span>
                    </th>
                    {RECIPIENT_CSV_LABELS.map((label: string, colIdx: number) => {
                      const fieldKey = `f${colIdx + 1}`;
                      const frozen = isFrozenDataCol(colIdx);
                      const lastFrozen = isLastFrozenDataCol(colIdx);
                      return (
                        <th key={`f-${colIdx}`}
                          className={`sticky top-[34px] ${frozen ? `${FROZEN_LEFT_CLASS[colIdx]} z-30` : 'z-20'} box-border px-1 py-1 bg-gray-50 border-b border-gray-200 min-w-[6.5rem] w-[6.5rem] ${lastFrozen ? 'border-r border-transparent meisai-freeze-edge' : 'shadow-[0_1px_0_0_rgb(229,231,235)]'}`}
                        >
                          <input
                            type="text"
                            value={columnFilters[fieldKey] ?? ''}
                            onChange={e => updateColumnFilter(fieldKey, e.target.value)}
                            placeholder={`${label}で絞込`}
                            title={`${label}で絞込`}
                            className="w-full border border-gray-300 rounded px-1.5 py-0.5 text-[11px] text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </th>
                      );
                    })}
                    <th className="sticky top-[34px] z-20 box-border px-2 py-1 bg-gray-50 border-b border-gray-200 w-16 min-w-[4rem]">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRowIndices.length === 0 ? (
                    <tr>
                      <td colSpan={1 + RECIPIENT_CSV_LABELS.length + 1}
                        className="px-4 py-6 text-center text-sm text-gray-500 bg-white">
                        フィルタ条件に一致する明細がありません
                      </td>
                    </tr>
                  ) : null}
                  {visibleRowIndices.map((rowIdx, visPos) => {
                    const row = detailRows[rowIdx];
                    const canSelectRow = !isNew && !commentOnly;
                    const isSelected = canSelectRow && selectedRowIdx === rowIdx;
                    const rowDeceased = isDetailRowDeceased(detailRows[rowIdx], deceasedMemberCodes);
                    // 選択行(青)を最優先。未選択の死亡行は控えめなローズ色で着色。
                    // bg-* は1つに絞る（bg-white と bg-rose-50 を併記すると CSS 出力順依存になるため）。
                    const stripe = isSelected
                      ? 'bg-blue-100'
                      : (rowDeceased
                          ? 'bg-rose-50'
                          : (rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100'));
                    return (
                      <tr key={rowIdx} className={stripe}>
                        <td
                          className={`sticky left-0 z-10 box-border px-2 py-1 border-b border-r text-gray-800 font-mono text-right w-12 min-w-[3rem] ${stripe} ${canSelectRow ? 'border-gray-200 cursor-pointer hover:bg-blue-100' : 'border-gray-200'}`}
                          onClick={canSelectRow ? () => toggleSelectedRow(rowIdx) : undefined}
                          title={canSelectRow ? (isSelected ? 'クリックで選択解除' : 'クリックでこの行を選択（上下に行を挿入できます）') : undefined}
                        >
                          {rowDeceased ? (
                            <span className="mr-0.5 text-red-600" title="この世帯員は死亡として登録されています">死</span>
                          ) : null}
                          {rowIdx + 1}
                        </td>
                        {RECIPIENT_CSV_LABELS.map((_: string, colIdx: number) => {
                          const fieldKey = `f${colIdx + 1}`;
                          const cellErr = fieldErrors.detailCells[`${rowIdx}-${fieldKey}`];
                          const frozen = isFrozenDataCol(colIdx);
                          const lastFrozen = isLastFrozenDataCol(colIdx);
                          return (
                            <td key={`r-${rowIdx}-c-${colIdx}`} className={`box-border p-0 border-b border-gray-100 align-top min-w-[6.5rem] w-[6.5rem] ${stripe} ${frozen ? `sticky ${FROZEN_LEFT_CLASS[colIdx]} z-10` : ''} ${lastFrozen ? 'border-r border-transparent meisai-freeze-edge' : ''}`}>
                              <input
                                type="text"
                                value={row[fieldKey] ?? ''}
                                onChange={e => updateDetailCell(rowIdx, fieldKey, e.target.value)}
                                onKeyDown={e => handleCellKeyDown(e, visPos, colIdx)}
                                data-cell-row={visPos}
                                data-cell-col={colIdx}
                                aria-invalid={!!cellErr}
                                title={cellErr ? `${RECIPIENT_CSV_LABELS[colIdx]}: ${cellErr}` : RECIPIENT_CSV_LABELS[colIdx]}
                                maxLength={STRING_FIELD_MAX_LENGTH[fieldKey]}
                                // scroll-mt: sticky 表头(~68px)分。scroll-ml: 矢印キーで左右移動した際、
                                // focus セルが左固定列(No.48px + f1-f4×104px = 464px)の下に隠れないよう左余白を確保。
                                // 固定列自身は常時可視なので付けない（横スクロールの誤作動防止）。
                                className={`${detailCellInputClass(!!cellErr)} scroll-mt-[68px] ${frozen ? '' : 'scroll-ml-[464px]'}`}
                              />
                            </td>
                          );
                        })}
                        <td className={`box-border px-2 py-1 border-b border-gray-100 text-center w-16 min-w-[4rem] ${stripe}`}>
                          <button type="button" onClick={() => removeDetailRow(rowIdx)}
                            className="text-xs text-red-600 hover:text-red-800 hover:underline cursor-pointer">
                            削除
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DetailErrorSummary entries={fieldErrors.detailEntries} lineLabel="明細" attached />
        </div>
        </fieldset>

        {/* アクションボタン */}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {draftSavedAt && (
            <span className="mr-auto text-xs text-gray-500" title="この端末に保存された下書きです">
              下書き保存済み {formatDateTime(draftSavedAt)}
            </span>
          )}
          <button type="button" onClick={() => navigate(isNew ? (baseCrumb.page || 'recipient-search') : 'recipient-detail', isNew ? undefined : detailOpts)}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer">
            キャンセル
          </button>
          <button type="button" onClick={handleManualDraftSave} disabled={saving}
            title="入力途中の内容をこの端末に一時保存します（本保存ではありません）"
            className="px-6 py-2 border border-blue-600 text-blue-700 bg-white rounded-lg hover:bg-blue-50 transition shadow-sm cursor-pointer disabled:opacity-50">
            一時保存
          </button>
          <button type="button" onClick={() => void save()} disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer disabled:opacity-50">
            保存
          </button>
        </div>
      </div>

      <NoticeDialog
        open={considerationAlertOpen}
        title="要配慮の受給者です"
        message={'この受給者は「要配慮」に指定されています。\n対応にあたっては内容に十分ご注意ください。'}
        confirmLabel="確認しました"
        variant="warning"
        onClose={() => setConsiderationAlertOpen(false)}
      />

      <ConfirmDialog
        open={showResetConfirm}
        title="案件ステータスの巻き戻し確認"
        message={'現在のステータスは「' + (d?.paymentState ?? '') + '」です。\n'
          + `受給者情報を保存すると、案件ステータスは「${CASE_STATUSES.AWAITING_TRANSFER_LIST}」に戻ります。\n保存しますか？`}
        confirmLabel="保存する"
        cancelLabel="キャンセル"
        variant="danger"
        loading={saving}
        onConfirm={() => void doSave()}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
};

export default RecipientEdit;
